import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { LEVEL_META, STATUS_META } from "@/lib/constants";
import { Lock, Sparkles, Search, Hammer, Route, ArrowRight, Clock, Network, Globe } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { EmailVerifyBanner } from "@/components/EmailVerifyBanner";

const CURRICULUM_PAIRS = {
  "safety-bypass-jailbreak": { to: "building-guardrails", label: "Now defend what you broke" },
  "building-guardrails": { to: "safety-bypass-jailbreak", label: "Revisit the attacks you blocked" },
};

const FILTERS = ["all", "beginner", "intermediate", "advanced", "developer", "supplychain", "infra", "claudecode101"];
const FILTER_LABELS = { all: "All", beginner: "AI Security Analyst", intermediate: "AI Red Teamer", advanced: "AI Red Team Operator", developer: "AI AppSec Engineer", supplychain: "AI Supply Chain Security", infra: "AI Infrastructure Security", claudecode101: "Claude Code 101" };

export default function LabCatalog() {
  const { user } = useAuth();
  const [labs, setLabs] = useState([]);
  const [search, setSearch] = useState("");
  const [params, setParams] = useSearchParams();
  const activeLevel = params.get("level") || "all";

  useEffect(() => {
    api.get("/labs").then((r) => setLabs(r.data.labs));
  }, []);

  const filtered = useMemo(() => {
    return labs.filter((l) => {
      if (activeLevel !== "all" && l.level !== activeLevel) return false;
      if (search && !(`${l.title} ${l.description}`.toLowerCase().includes(search.toLowerCase()))) return false;
      return true;
    });
  }, [labs, activeLevel, search]);

  const canAccess = (lab) =>
    lab.is_free || user?.role === "admin" || user?.subscription_status === "active";

  return (
    <div className="bg-zinc-50 dark:bg-[#050505] min-h-screen text-zinc-900 dark:text-white transition-colors">
      {/* HEADER */}
      <div className="sticky top-16 z-40 bg-zinc-50/95 dark:bg-[#050505]/95 backdrop-blur-xl border-b border-zinc-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF3B30] mb-2">// Lab catalog</div>
              <h1 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl">Choose your target</h1>
              <p className="mt-2 text-xs font-mono text-zinc-500">{labs.length} labs across seven tracks · new labs every week</p>
            </div>
            <div className="relative lg:w-72">
              <Search className="w-4 h-4 text-zinc-400 dark:text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search labs…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="labs-search-input"
                className="pl-9 rounded-none bg-white dark:bg-[#0A0A0A] border-zinc-300 dark:border-white/15 text-zinc-900 dark:text-white font-mono text-sm placeholder:text-zinc-400 dark:placeholder:text-zinc-600 focus-visible:ring-[#FF3B30]"
              />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap mt-5" data-testid="catalog-filters">
            {FILTERS.map((lvl) => {
              const active = activeLevel === lvl;
              return (
                <button
                  key={lvl}
                  onClick={() => setParams(lvl === "all" ? {} : { level: lvl })}
                  data-testid={`filter-level-${lvl}`}
                  className={`rounded-none border px-4 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                    active
                      ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                      : "bg-transparent border-zinc-300 dark:border-white/15 text-zinc-500 dark:text-zinc-400 hover:border-zinc-500 dark:hover:border-white/40 hover:text-zinc-900 dark:hover:text-white"
                  }`}
                >
                  {FILTER_LABELS[lvl] || lvl}
                </button>
              );
            })}
          </div>
          {activeLevel === "claudecode101" && (
            <Link
              to="/courses/claude-code-101"
              className="mt-5 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
              data-testid="catalog-cc101-course-link"
            >
              View the full 10-lab course &rarr;
            </Link>
          )}
        </div>
      </div>

      {/* GRID */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <EmailVerifyBanner />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 dark:bg-white/10 border border-zinc-200 dark:border-white/10">
          {filtered.map((lab) => {
            const lm = LEVEL_META[lab.level] || LEVEL_META.beginner;
            const sm = STATUS_META[lab.status] || STATUS_META.not_started;
            const locked = !canAccess(lab);
            const comingSoon = lab.is_built === false;
            const isNetwork = lab.lab_kind === "network";
            const interactive = !comingSoon;
            const CardWrap = interactive ? Link : "div";
            const cardProps = interactive
              ? { to: `/labs/${lab.slug}`, "data-testid": `lab-card-${lab.slug}` }
              : { "aria-disabled": true, "data-testid": `lab-card-${lab.slug}` };
            const pair = CURRICULUM_PAIRS[lab.slug];
            const partner = pair ? labs.find((x) => x.slug === pair.to) : null;

            return (
              <CardWrap
                key={lab.id}
                {...cardProps}
                className={`group relative block bg-white dark:bg-[#0A0A0A] p-6 transition-all ${
                  interactive ? "hover:bg-zinc-50 dark:hover:bg-[#101010] hover:-translate-y-1" : "cursor-not-allowed"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${lm.chipThemed}`}>{lm.label}</span>
                  <div className="flex gap-1.5">
                    <span className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${
                      isNetwork
                        ? "border-[#FF3B30]/30 text-[#FF3B30] bg-[#FF3B30]/10"
                        : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-white/5"
                    }`}>
                      {isNetwork ? <Network className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                      {isNetwork ? "VPN" : "Browser"}
                    </span>
                    {!comingSoon && lab.is_free && (
                      <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-[#00E5FF]/10 text-[#0891b2] dark:text-[#00E5FF] border border-[#00E5FF]/30">
                        <Sparkles className="w-3 h-3" /> Free
                      </span>
                    )}
                  </div>
                </div>

                <h3 className="font-display font-bold uppercase tracking-tight text-lg leading-tight text-zinc-900 dark:text-white group-hover:text-[#FF3B30] transition-colors">
                  {lab.title}
                </h3>
                <p className="text-xs font-mono text-zinc-600 dark:text-zinc-400 mt-2 leading-relaxed line-clamp-2">{lab.description}</p>

                <div className="flex items-center justify-between mt-5 pt-4 border-t border-zinc-200 dark:border-white/5">
                  <span className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-500">
                    <Clock className="w-3.5 h-3.5" /> {lab.duration_minutes} min
                  </span>
                  {comingSoon ? (
                    <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-600 dark:text-amber-300" data-testid={`coming-soon-${lab.slug}`}>
                      <Hammer className="w-3 h-3" /> Coming soon
                    </span>
                  ) : (
                    <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${sm.chipThemed}`}>{sm.label}</span>
                  )}
                </div>

                {pair && partner && interactive && (
                  <div className="mt-4 flex items-center gap-2 text-[11px] font-mono text-[#0891b2] dark:text-[#00E5FF] border border-[#00E5FF]/20 bg-[#00E5FF]/5 px-3 py-2" data-testid={`curriculum-path-${lab.slug}`}>
                    <Route className="w-3.5 h-3.5 shrink-0" />
                    <span className="shrink-0">{pair.label}</span>
                    <ArrowRight className="w-3.5 h-3.5 ml-auto shrink-0" />
                  </div>
                )}

                {!comingSoon && locked && (
                  <div className="absolute inset-0 bg-white/85 dark:bg-black/80 backdrop-blur-[2px] flex flex-col items-center justify-center text-center px-6 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Lock className="w-6 h-6 text-[#FF3B30] mb-2" />
                    <div className="text-xs font-mono uppercase tracking-wider text-zinc-900 dark:text-white">Subscribe to unlock</div>
                    <div className="text-[11px] font-mono text-zinc-500 mt-1">Included in every plan</div>
                  </div>
                )}
              </CardWrap>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-20 font-mono text-sm text-zinc-500">No labs match those filters.</div>
        )}
      </div>
    </div>
  );
}
